import { useState, useEffect, useRef, useCallback, useMemo } from 'react'
import { useLiturgyData } from '../hooks/useLiturgyData'
import CategoryList from '../components/CategoryList'
import { useLanguage } from '../context/LanguageContext'
import LiturgySection from '../components/LiturgySection'
import UpgradePrompt from '../components/UpgradePrompt'
import RedeemDialog from '../components/RedeemDialog'
import ShowTextToggle from '../components/ShowTextToggle'
import AiAssistant from '../components/AiAssistant'
import Settings from '../components/Settings'
import Guide from './Guide'
import { useBookmarks } from '../hooks/useBookmarks'
import { useProgress } from '../hooks/useProgress'
import { useUsageTracking } from '../hooks/useUsageTracking'
import { ROLES, assignRoles, getRoleLabel } from '../components/RoleIcon'
import { t, tEn, getTextField } from '../i18n/translations'
import { useAuth } from '../context/AuthContext'
import { FREE_LESSON_COUNT } from '../lib/access'
import { consumePendingRedeem } from '../lib/redeemEntry'

// Extract the slide number from a section ID (e.g. "CD-1-Track2-Slide_8" → "8")
function getSlideNumber(sectionId) {
  if (!sectionId) return null
  const match = String(sectionId).match(/Slide[_-](\d+)/i)
  return match ? match[1] : null
}

function getCategoryDisplayName(category) {
  const melody = (category.id === 3 || category.id === 4) ? t('categoryEzli') : t('categoryGeez')
  const group = (category.id === 1 || category.id === 3) ? t('sirateKidase') : t('kidaseHawariat')
  return `${group} (${melody})`
}

export default function Home() {
  const { translationField } = useLanguage()
  const [readerOpen, setReaderOpen] = useState(false)
  const [search, setSearch] = useState('')
  const listRef = useRef(null)
  const lastLessonButton = useRef(null)
  const { categories, loading, error } = useLiturgyData()
  const [activeCategoryId, setActiveCategoryId] = useState(() => {
    const saved = localStorage.getItem('lastCategoryId')
    return saved ? parseInt(saved) : null
  })
  const [selectedSectionId, setSelectedSectionId] = useState(() => {
    const saved = localStorage.getItem('lastSectionId')
    if (!saved) return null
    // Try as number first (for numeric IDs), fall back to string (for "CD-1-Track2-Slide_8" style)
    const num = parseInt(saved)
    return (!isNaN(num) && String(num) === saved) ? num : saved
  })
  const [roleFilter, setRoleFilter] = useState(() => {
    return localStorage.getItem('sk_role_filter') || 'all'
  })
  const [showUpgrade, setShowUpgrade] = useState(false)
  // Opened by ?redeem=1, and again after a sign-in redirect returns the
  // teacher to a freshly loaded page. consumePendingRedeem clears the flag as
  // it reads it, so the dialog opens once and not on every later visit.
  const [showRedeem, setShowRedeem] = useState(() => consumePendingRedeem())
  const [showSettings, setShowSettings] = useState(false)
  const [showGuide, setShowGuide] = useState(false)
  const { resumePoint, updateResume } = useBookmarks()
  const { updateProgress, getProgress, getCategoryStats } = useProgress()
  const { trackPlay, trackProgress, trackEnd, flushAll } = useUsageTracking()
  const prevSectionRef = useRef(null)

  // Save role filter to localStorage
  useEffect(() => {
    localStorage.setItem('sk_role_filter', roleFilter)
  }, [roleFilter])

  // Save category selection to localStorage
  useEffect(() => {
    if (activeCategoryId) {
      localStorage.setItem('lastCategoryId', activeCategoryId.toString())
    }
  }, [activeCategoryId])

  // Save section selection to localStorage
  useEffect(() => {
    if (selectedSectionId) {
      localStorage.setItem('lastSectionId', selectedSectionId.toString())
    }
  }, [selectedSectionId])

  // Both layouts start with a selected category and a browsable lesson list.
  useEffect(() => {
    if (categories.length > 0 && activeCategoryId === null) {
      setActiveCategoryId(categories[0].id)
    }
  }, [categories])

  const activeCategory = categories.find(c => c.id === activeCategoryId)
  const allChants = useMemo(() => assignRoles(activeCategory?.chants || []), [activeCategory])
  const { isPremium } = useAuth()
  // The free lessons of THIS category, by id. A Set built once beats calling
  // findIndex for every row — the largest category has 145 of them.
  const freeLessonIds = useMemo(
    () => new Set(allChants.slice(0, FREE_LESSON_COUNT).map(c => c.id)),
    [allChants],
  )
  const chants = useMemo(() => {
    if (roleFilter === 'all') return allChants
    return allChants.filter(c => c._role === roleFilter)
  }, [allChants, roleFilter])

  const visibleLessons = useMemo(() => {
    const query = search.trim().toLocaleLowerCase()
    return allChants.filter(chant => !query || [chant._geeztext, chant._latintext, chant._amharictext, chant._tigrinyatext, chant._englishtext, getSlideNumber(chant.id)].some(value => String(value || '').toLocaleLowerCase().includes(query)))
  }, [allChants, search])

  // Update resume point when lesson changes
  useEffect(() => {
    if (selectedSectionId && chants.length > 0 && activeCategoryId) {
      const chant = chants.find(c => c.id === selectedSectionId)
      if (chant) updateResume(chant, activeCategoryId)
    }
  }, [selectedSectionId, chants, activeCategoryId, updateResume])

  // Track lesson session changes for usage analytics
  useEffect(() => {
    const prev = prevSectionRef.current
    if (prev && prev !== selectedSectionId) {
      trackEnd(prev)
    }
    if (selectedSectionId && selectedSectionId !== prev) {
      trackPlay(selectedSectionId, activeCategoryId)
    }
    prevSectionRef.current = selectedSectionId
  }, [selectedSectionId, activeCategoryId, trackPlay, trackEnd])

  // Flush usage tracking on unmount / page close
  useEffect(() => {
    const handleBeforeUnload = () => flushAll()
    window.addEventListener('beforeunload', handleBeforeUnload)
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload)
      flushAll()
    }
  }, [flushAll])

  // Lesson navigation (prev/next)
  const selectedIndex = chants.findIndex(c => c.id === selectedSectionId)
  const goToLesson = useCallback((index) => {
    if (index >= 0 && index < chants.length) {
      setSelectedSectionId(chants[index].id)
      // Scroll the selected lesson into view
      setTimeout(() => {
        const el = document.querySelector('.liturgy-section.selected')
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' })
      }, 50)
    }
  }, [chants])

  const goPrev = useCallback(() => {
    if (selectedIndex > 0) goToLesson(selectedIndex - 1)
  }, [selectedIndex, goToLesson])

  const goNext = useCallback(() => {
    if (selectedIndex < chants.length - 1) goToLesson(selectedIndex + 1)
    else if (selectedIndex === -1 && chants.length > 0) goToLesson(0)
  }, [selectedIndex, chants.length, goToLesson])

  // Swipe detection for mobile lesson navigation
  const touchStartRef = useRef(null)
  function handleTouchStart(e) {
    touchStartRef.current = { x: e.touches[0].clientX, y: e.touches[0].clientY }
  }
  function handleTouchEnd(e) {
    if (!touchStartRef.current) return
    const dx = e.changedTouches[0].clientX - touchStartRef.current.x
    const dy = e.changedTouches[0].clientY - touchStartRef.current.y
    touchStartRef.current = null
    // Only trigger on horizontal swipes (dx > 60px, more horizontal than vertical)
    if (Math.abs(dx) > 60 && Math.abs(dx) > Math.abs(dy) * 1.5) {
      if (dx < 0) goNext()  // swipe left = next
      else goPrev()          // swipe right = prev
    }
  }

  // Validate that selected section exists in the active category
  useEffect(() => {
    if (selectedSectionId && chants.length > 0) {
      const sectionExists = chants.some(c => c.id === selectedSectionId)
      if (!sectionExists) {
        // Section doesn't exist in this category, clear it
        setSelectedSectionId(null)
        localStorage.removeItem('lastSectionId')
      }
    }
  }, [activeCategoryId, chants, selectedSectionId])

  if (loading) {
    return (
      <div className="loading">
        <div className="loading-spinner" />
        <p>{t('loading')}</p>
      </div>
    )
  }

  if (error) {
    return (
      <div className="error">
        <p>{t('errorLoading')} {error}</p>
        <button onClick={() => window.location.reload()}>{t('retry')}</button>
      </div>
    )
  }

  if (categories.length === 0) {
    return (
      <div className="empty">
        <p>{t('noData')}</p>
      </div>
    )
  }

  return (
    <main className="home">
      <div className="study-heading">
        <h2>{t('browseTitle')}</h2>
        <div className="study-tools">
      <AiAssistant lessonContext={selectedIndex >= 0 ? {
        geezText: chants[selectedIndex]?._geeztext,
        translationText: chants[selectedIndex]?.[getTextField()] || chants[selectedIndex]?._tigrinyatext || chants[selectedIndex]?._amharictext,
        englishText: chants[selectedIndex]?._englishtext,
        categoryName: activeCategory ? getCategoryDisplayName(activeCategory) : '',
        role: chants[selectedIndex]?._geeztext?.match(/^(ካህን|ዲያቆን|ሕዝብ)/)?.[1] || null
      } : null} />
          <button aria-label={t('browsePrayer')} title={t('browsePrayer')} className="opening-prayer-link" onClick={() => { localStorage.removeItem('sk_welcome_seen'); window.location.reload() }}><svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7"><circle cx="12" cy="12" r="9"/><path d="m10 8 6 4-6 4Z"/></svg><span>{t('browsePrayer')}</span></button>
          <button aria-label={t('browseGuide')} title={t('browseGuide')} onClick={() => setShowGuide(true)}><svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7"><path d="M12 5C8 2 5 3 3 4v15c3-2 6-2 9 0 3-2 6-2 9 0V4c-3-1-6-2-9 1Zm0 0v14"/></svg><span>{t('browseGuide')}</span></button>
          <button aria-label={t('browseSettings')} title={t('browseSettings')} onClick={() => setShowSettings(true)}><svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7"><path d="M4 7h16M4 17h16"/><circle cx="9" cy="7" r="3" fill="var(--bg)"/><circle cx="15" cy="17" r="3" fill="var(--bg)"/></svg><span>{t('browseSettings')}</span></button>
        </div>
      </div>


      <div className={'study-workspace ' + (readerOpen ? 'reader-open' : '')}>
        <section className="study-browser" aria-label={t('browseTitle')}>
      {resumePoint && <button className="continue-learning" onClick={() => {
        setRoleFilter('all')
        setActiveCategoryId(resumePoint.categoryId)
        setSelectedSectionId(resumePoint.sectionId)
        setReaderOpen(true)
      }}><span className="continue-icon" aria-hidden="true">▶</span><span><strong>{t('browseContinue')}</strong><span className="continue-preview">{resumePoint.geezPreview}</span></span><span aria-hidden="true">→</span></button>}
          <aside className="study-categories">
            <h3>{t('categoriesTitle')}</h3>
            <CategoryList categories={categories} activeId={activeCategoryId} getCategoryStats={getCategoryStats} onSelect={id => {
              setActiveCategoryId(id)
              setSelectedSectionId(null)
              setRoleFilter('all')
              setReaderOpen(false)
              if (listRef.current) listRef.current.scrollTop = 0
            }} />
          </aside>
          <div className="study-lessons">
            <div className="lesson-list-heading"><h3>{t('browseLessons')}</h3><span>{allChants.length}</span></div>
            <label className="lesson-search"><span>{t('browseSearch')}</span><input type="search" value={search} onChange={e => setSearch(e.target.value)} placeholder={t('browseSearch')} /></label>
            <div className="lesson-index" ref={listRef}>
              {visibleLessons.map(chant => {
                const progress = getProgress(chant.id)
                const title = (chant[translationField] || chant._geeztext || '').replace(/^\+?\s*(?:Hizb|People|Priest|Deacon|Together|kahn|behibret|ካህን|ሕዝብ|ዲያቆን|በሕብረት)[፡:]\s*/i, '').split(/\s+/).slice(0, 2).join(' ')
                const locked = !isPremium && !freeLessonIds.has(chant.id)
                return <button key={chant.id} title={title} className={'lesson-index-item ' + (selectedSectionId === chant.id ? 'active' : '') + (locked ? ' locked' : '')} aria-current={selectedSectionId === chant.id ? 'true' : undefined} onClick={e => {
                  lastLessonButton.current = e.currentTarget
                  setRoleFilter('all')
                  setSelectedSectionId(chant.id)
                  setReaderOpen(true)
                }}><span className="lesson-number">{getSlideNumber(chant.id)}</span><span className="lesson-index-title">{title}<small className="sr-only">{progress.learned ? t('browseCompleted') : t('browseListen')}</small></span><span className="lesson-status" aria-hidden="true">{locked ? (
                  <svg className="lesson-lock" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <rect x="4" y="11" width="16" height="10" rx="2" /><path d="M8 11V7a4 4 0 0 1 8 0v4" />
                  </svg>
                ) : progress.learned ? '✓' : '›'}</span>{locked && <span className="sr-only">{t('lessonLocked')}</span>}</button>
              })}
              {visibleLessons.length === 0 && <p className="no-lessons">{t('browseNoResults')}</p>}
            </div>
          </div>
        </section>

        <section className="study-reader" aria-label={t('browseReader')}>
          <div className="reader-toolbar">
            <button className="reader-back" onClick={() => { setReaderOpen(false); requestAnimationFrame(() => { const target = lastLessonButton.current?.isConnected ? lastLessonButton.current : listRef.current?.querySelector('button'); target?.focus({ preventScroll: true }); listRef.current?.closest('.study-browser')?.scrollIntoView({ block: 'start' }) }) }}>← {t('browseBack')}</button>
            <span>{activeCategory ? getCategoryDisplayName(activeCategory) : ''}</span>
            <div className="reader-arrows"><button onClick={goPrev} disabled={selectedIndex <= 0} aria-label={t('previousLesson')}>←</button><span>{selectedIndex >= 0 ? selectedIndex + 1 : '–'}/{chants.length}</span><button onClick={goNext} disabled={selectedIndex >= chants.length - 1} aria-label={t('nextLesson')}>→</button></div>
          </div>
          {selectedIndex >= 0 ? <>
            <div className="reader-options">
              <details className="voice-options"><summary>{t('browseVoices')} <span>{getRoleLabel(roleFilter)}</span></summary><div className="role-filter">{ROLES.map(r => <button key={r.key} aria-pressed={roleFilter === r.key} className={'role-filter-btn ' + (roleFilter === r.key ? 'active' : '')} onClick={() => {
                setRoleFilter(r.key)
                const filtered = r.key === 'all' ? allChants : allChants.filter(c => c._role === r.key)
                if (!filtered.some(c => c.id === selectedSectionId)) setSelectedSectionId(filtered[0]?.id || null)
              }}>{getRoleLabel(r.key)}</button>)}</div></details>
              <ShowTextToggle />
            </div>
            <div className="sections-list" onTouchStart={handleTouchStart} onTouchEnd={handleTouchEnd}>
              <LiturgySection key={chants[selectedIndex].id} section={chants[selectedIndex]} categoryId={activeCategoryId} sequenceNumber={getSlideNumber(chants[selectedIndex].id)} isSelected={true} onSelect={() => {}} onLockedClick={() => setShowUpgrade(true)} lessonProgress={getProgress(chants[selectedIndex].id)} onProgress={(cur, dur) => { updateProgress(chants[selectedIndex].id, cur, dur); trackProgress(chants[selectedIndex].id, cur, dur) }} lessonIndex={allChants.findIndex(c => c.id === chants[selectedIndex].id)} />
            </div>
          </> : <div className="reader-empty"><span aria-hidden="true">ሀ</span><h3>{t('browseChoose')}</h3><p>{t('browseChooseHint')}</p>{roleFilter !== 'all' && <button onClick={() => setRoleFilter('all')}>{t('browseLessons')}</button>}</div>}
        </section>
      </div>

      {showUpgrade && <UpgradePrompt onClose={() => setShowUpgrade(false)} />}
      {showRedeem && <RedeemDialog onClose={() => setShowRedeem(false)} />}

      {showSettings && (
        <Settings
          onClose={() => setShowSettings(false)}
          previewText={selectedIndex >= 0 ? chants[selectedIndex]?._geeztext?.substring(0, 50) : undefined}
        />
      )}

      {showGuide && (
        <div className="settings-overlay" onClick={() => setShowGuide(false)}>
          <div className="settings-panel guide-panel" onClick={e => e.stopPropagation()}>
            <Guide onBack={() => setShowGuide(false)} />
          </div>
        </div>
      )}




    </main>
  )
}
